import React, { memo, useState } from 'react';
import { Avatar, Menu, MenuItem } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { enqueueSnackbar } from 'notistack';
import moment from 'moment';

import FullscreenLoader from 'modules/views/shared/FullscreenLoader';
import HelpModal from 'modules/views/shared/HelpModal';
import { ReactComponent as GoToProjectsIcon } from 'assets/icons/GoToProjects.svg';
import { ReactComponent as LogoutIcon } from 'assets/icons/Logout.svg';
import { ReactComponent as ArrowDownIcon } from 'assets/icons/ArrowDown.svg';
import { ReactComponent as HelpIcon } from 'assets/icons/HelpGray.svg';
import { ReactComponent as Person } from 'assets/icons/PersonGray.svg';

import { useAppDispatch, useAppSelector } from 'modules/state-mgmt/store';
import { logoutUser } from 'modules/state-mgmt/user';
import { prettifyErrorResponse } from 'utils';

import { ROUTES } from 'constants/routes';
import { useStylesWorkerAndAdminMenu } from '../styles';
import { actionMapForOtherLoaders } from 'constants/actions';

const packageJson = require('../../../../../../package.json');

export interface IWorkerMenuProps {
  styleClass?: string;
}

const WorkerMenu = ({ styleClass = '' }: IWorkerMenuProps) => {
  const classes = useStylesWorkerAndAdminMenu();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { worker, isLogoutLoading } = useAppSelector(state => ({
    worker: state.onboarding,
    isLogoutLoading: state.loader.otherLoaders?.[actionMapForOtherLoaders.logoutUser]?.isLoading,
  }));

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [isHelpModalVisible, setIsHelpModalVisible] = useState(false);
  const isOpen = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const onGoToProjects = () => {
    navigate(ROUTES.PROJECT_LIST.path);
  };

  const onGoToProfile = () => {
    navigate(ROUTES.MY_PROFILE.path);
  };

  const onLogout = async () => {
    try {
      await dispatch(logoutUser({ sessionExpired: false })).unwrap();
      navigate(ROUTES.LOGIN.path);
    } catch (error) {
      enqueueSnackbar(prettifyErrorResponse(error), { variant: 'error' });
    }
  };

  return (
    <div className={`${classes.parent} ${styleClass}`}>
      <div onClick={handleClick} className={classes.avatarContainer} data-testid="worker-menu-button">
        <Avatar className={classes.avatar} src={worker.pictureUrl || ''}>
          {worker.firstName?.slice(0, 1).toUpperCase() || ''}
        </Avatar>
        <ArrowDownIcon className={isOpen ? classes.arrowIconRotated : classes.arrowIcon} />
      </div>
      <Menu
        id="worker-account-menu"
        anchorEl={anchorEl}
        open={isOpen}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        slotProps={{ paper: { elevation: 0 } }}
        className={classes.menu}
        disableAutoFocusItem={true}
        disableScrollLock={true}
        onClose={handleClose}
        onClick={handleClose}
      >
        <MenuItem data-testid="worker-menu-my-profile" disableRipple={true} onClick={onGoToProfile}>
          <Person />
          {t('header.myProfile')}
        </MenuItem>
        <MenuItem data-testid="worker-menu-go-to-projects" disableRipple={true} onClick={onGoToProjects}>
          <GoToProjectsIcon />
          {t('header.goToProjects')}
        </MenuItem>
        <MenuItem
          className={classes.hideMenuItem}
          data-testid="worker-menu-help"
          disableRipple={true}
          onClick={() => setIsHelpModalVisible(true)}
        >
          <HelpIcon />
          {t('common.help')}
        </MenuItem>
        <MenuItem data-testid="worker-menu-logout" disableRipple={true} onClick={onLogout}>
          <LogoutIcon />
          {t('header.logout')}
        </MenuItem>
        <div className={classes.buildInfo}>
          V{packageJson.buildVersion}
          {packageJson.buildDate && ` - ${moment(packageJson.buildDate).format('MMMM D,YYYY')}`}
        </div>
      </Menu>
      {isLogoutLoading && <FullscreenLoader transparent={true} text={t('header.loggingOut')} />}
      <HelpModal isVisible={isHelpModalVisible} onClose={() => setIsHelpModalVisible(false)} />
    </div>
  );
};

export default memo(WorkerMenu);
